import React, { useState, useEffect } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { pluralise } from '../../utils/stringUtils';
import { getCurrencySymbol, numberToMoney } from '../../utils/stringUtils';

import { Button, Checkbox, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction, Paper, withStyles, Typography, InputBase, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment } from '@material-ui/core';

import { ChevronLeft as MoveLeftIcon, ChevronRight as MoveRightIcon, FirstPage as MoveAllLeftIcon, LastPage as MoveAllRightIcon, Edit as EditIcon } from '@material-ui/icons';

import { Search as SearchIcon, Clear as ClearSearchIcon } from '@material-ui/icons/';

import styles from './styles';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  labelLeft: string;
  labelRight: string;
  description?: string;
  currentItems: ModifierTransferListItem[];
  allItems: ModifierTransferListItem[];
  availableItems?: ModifierTransferListItem[]; 
  handleTransfer: (left: ModifierTransferListItem[], right: ModifierTransferListItem[]) => void;
  currency: string;
  locale: string;
  preselectedCategoryModifiers?: boolean;
  onEditModifier?: (modifier: ModifierTransferListItem, updatedModifier: ModifierTransferListItem) => void;
  allowCategoryModifierRemoval?: boolean;
}

export interface ModifierTransferListItem {
  id: string;
  label: string;
  price: number;
  tax: number;
  isCategoryModifier?: boolean;
  assignmentId?: string;
}

const not = (a: ModifierTransferListItem[], b: ModifierTransferListItem[]) => a.filter((value) => b.indexOf(value) === -1);

const intersection = (a: ModifierTransferListItem[], b: ModifierTransferListItem[]) => a.filter((value) => b.indexOf(value) !== -1);

const ModifiersTransferList = ({ 
  classes, 
  labelLeft, 
  labelRight, 
  description, 
  currentItems, 
  allItems, 
  availableItems,
  handleTransfer, 
  currency, 
  locale,
  preselectedCategoryModifiers,
  onEditModifier,
  allowCategoryModifierRemoval = true
}: Props): React.ReactElement => {
  const [checked, setChecked] = useState<ModifierTransferListItem[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [left, setLeft] = useState<ModifierTransferListItem[]>([]);
  const [right, setRight] = useState<ModifierTransferListItem[]>(currentItems);
  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);
  
  // Edit modifier dialog state
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editingModifier, setEditingModifier] = useState<ModifierTransferListItem | null>(null);
  const [editFormData, setEditFormData] = useState<{ name: string; price: number; tax: number }>({ name: '', price: 0, tax: 0 });
  const [editNameError, setEditNameError] = useState<string | false>(false);
  const [showEditWarning, setShowEditWarning] = useState<boolean>(false);
  
  // Recompute the available (left) list when the pool or selection changes.
  // BLUM-5928: do NOT reset `right` here. Resetting it on every `allItems` change
  // (e.g. when a newly created modifier is appended to the pool) wiped the selected
  // list and caused the "Add modifier flashes and disappears" bug. `right` is reset
  // from `currentItems` in the effect below instead.
  useEffect(() => {
    if (availableItems) {
      setLeft(availableItems);
    } else if (allItems && allItems.length > 0) {
      const computedAvailable = allItems.filter((item) => !currentItems.some((current) => item.label === current.label && item.price === current.price && item.tax === current.tax));
      setLeft(computedAvailable);
    }
  }, [allItems, currentItems, availableItems]);

  // Reset the selected (right) list only when the parent's currentItems actually changes.
  useEffect(() => {
    setRight(currentItems);
  }, [currentItems]);
  
  useEffect(() => {
    let mounted = true;
    const rightIds = right.map((i) => i.id);
    const currentIds = currentItems.map((i) => i.id);
    const areSame = currentIds.every((id) => rightIds.includes(id)) && rightIds.every((id) => currentIds.includes(id));
    if (mounted && !areSame) {
      handleTransfer(left, right);
    }
    return () => {
      mounted = false;
    };
  }, [right]);

  const handleToggle = (value: ModifierTransferListItem) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    if (searchValue.length > 0) {
      setRight((i) => [...i, ...left.filter((i) => i.label.toLowerCase().includes(searchValue.toLowerCase()))]);
      setLeft(left.filter((i) => !i.label.toLowerCase().includes(searchValue.toLowerCase())));
    } else {
      setRight((i) => [...i, ...left]);
      setLeft([]);
    }
    setChecked([]);
  };

  const handleCheckedRight = () => {
    setRight((i) => [...i, ...leftChecked]);
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft((i) => [...i, ...rightChecked]);
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    // Filter out category modifiers if removal is not allowed
    const itemsToMove = allowCategoryModifierRemoval 
      ? right 
      : right.filter(item => !item.isCategoryModifier);
    setLeft((i) => [...i, ...itemsToMove]);
    setRight(allowCategoryModifierRemoval ? [] : right.filter(item => item.isCategoryModifier));
    setChecked([]);
  };

  const handleCheckedLeftFiltered = () => {
    // Filter out category modifiers if removal is not allowed
    const itemsToMove = allowCategoryModifierRemoval 
      ? rightChecked 
      : rightChecked.filter(item => !item.isCategoryModifier);
    setLeft((i) => [...i, ...itemsToMove]);
    setRight(not(right, itemsToMove));
    setChecked(not(checked, itemsToMove));
  };

  // Edit modifier handlers
  const handleOpenEditDialog = (item: ModifierTransferListItem, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingModifier(item);
    setEditFormData({
      name: item.label,
      price: item.price,
      tax: item.tax,
    });
    setEditNameError(false);
    setShowEditWarning(false);
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: string, value: any) => {
    let processedValue = value;
    if (field === 'price') {
      processedValue = Math.round(+value * 100);
    } else if (field === 'tax') {
      processedValue = Math.round(+value * 10);
    }
    if (field === 'name') {
      setEditNameError(false);
    }
    setEditFormData(prev => ({ ...prev, [field]: processedValue }));
  };

  const handleConfirmEdit = () => {
    if (!editingModifier || !onEditModifier) return;

    // Name is required; price of 0 is valid (BLUM-5927)
    if (!editFormData.name.trim()) {
      setEditNameError('Name is required');
      return;
    }

    const updatedModifier: ModifierTransferListItem = {
      ...editingModifier,
      label: editFormData.name,
      price: editFormData.price,
      tax: editFormData.tax,
    };

    // Call the parent's edit handler - let parent manage state updates
    // The parent will update its state, which flows back as currentItems prop
    onEditModifier(editingModifier, updatedModifier);

    setEditDialogOpen(false);
    setEditingModifier(null);
  };

  const handleProceedWithEdit = () => {
    setShowEditWarning(true);
  };

  const customList = (title: string, items: ModifierTransferListItem[], isRightList: boolean = false) => {
    const sortedItems = [...items].sort((a, b) => {
      if ((a.isCategoryModifier && b.isCategoryModifier) || (!a.isCategoryModifier && !b.isCategoryModifier)) {
        return a.label.localeCompare(b.label);
      }
      return a.isCategoryModifier ? -1 : 1;
    });

    return (
      <Paper className={classes.paper} elevation={0} variant="outlined">
        <Typography className={classes.listTitle} variant="subtitle1">
          {items.length} {title}
        </Typography>
        <Divider />
        <List dense component="div" role="list">
          {sortedItems.map((item, index) => {
            const labelId = `transfer-list-item-${item.id}-label`;
            const uniqueKey = `${item.id}-${index}`;
            return (
              <ListItem 
                key={uniqueKey} 
                role="listitem" 
                button 
                onClick={handleToggle(item)}
                className={item.isCategoryModifier ? classes.categoryModifierItem : undefined}
              >
                <ListItemIcon>
                  <Checkbox
                    data-qa="transfer-list-item-check-box"
                    checked={checked.indexOf(item) !== -1}
                    color="primary"
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemIcon>
                <ListItemText
                  data-qa="transfer-list-item-name"
                  id={labelId}
                  primary={item.label}
                  secondary={
                    <React.Fragment>
                      <Typography component="span" variant="body2" color="textPrimary">
                        {`${getCurrencySymbol(currency, locale)}${(item.price / 100).toFixed(2)}`}
                      </Typography>
                      <Typography component="span" variant="body2" color="textSecondary">
                        {` • Tax: ${(item.tax / 10).toFixed(1)}%`}
                      </Typography>
                      {item.isCategoryModifier && (
                        <Typography component="div" variant="body2" color="primary" className={classes.categoryModifierLabel}>
                          category modifier
                        </Typography>
                      )}
                    </React.Fragment>
                  }
                />
                {onEditModifier && isRightList && (
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      aria-label="edit" 
                      size="small"
                      onClick={(e) => handleOpenEditDialog(item, e)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
          <ListItem />
        </List>
      </Paper>
    );
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center" className={classes.root}>
        <Grid item xs={5}>
          <Paper variant="outlined" className={classes.search}>
            <InputBase
              className={classes.searchInput}
              placeholder="Search"
              value={searchValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value || '')}
              startAdornment={<SearchIcon className={classes.searchButton} />}
              endAdornment={<ClearSearchIcon className={classes.searchButton} onClick={() => setSearchValue('')} />}
              inputProps={{ 'aria-label': 'search list' }}
            />
          </Paper>
        </Grid>
        <Grid item xs={2} />
        <Grid item xs={5}>
          <Typography className={classes.description} variant="caption">
            {description}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center" className={classes.root}>
        <Grid item xs={5} className={classes.list}>
          {customList(
            labelLeft,
            left.filter((i) => i.label.toLowerCase().includes(searchValue.toLowerCase()))
          )}
        </Grid>
        <Grid item xs={2}>
          <Grid container direction="column" alignItems="center">
            <Button
              data-qa="movel-all-right-button"
              variant="outlined"
              size="small"
              color="primary"
              className={classes.button}
              onClick={handleAllRight}
              disabled={left.length === 0}
              aria-label="move all right">
              <MoveAllRightIcon />
            </Button>
            <Button
              data-qa="move-selected-right-button"
              variant="outlined"
              size="small"
              color="primary"
              className={classes.button}
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right">
              <MoveRightIcon />
            </Button>
            <Button
              data-qa="move-selected-left-button"
              variant="outlined"
              size="small"
              color="primary"
              className={classes.button}
              onClick={handleCheckedLeftFiltered}
              disabled={rightChecked.length === 0 || (!allowCategoryModifierRemoval && rightChecked.every(item => item.isCategoryModifier))}
              aria-label="move selected left">
              <MoveLeftIcon />
            </Button>
            <Button
              data-qa="move-all-left-button"
              variant="outlined"
              size="small"
              color="primary"
              className={classes.button}
              onClick={handleAllLeft}
              disabled={right.length === 0}
              aria-label="move all left">
              <MoveAllLeftIcon />
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={5} className={classes.list}>
          {customList(labelRight, right, true)}
        </Grid>
      </Grid>

      {/* Edit Modifier Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Modifier</DialogTitle>
        <DialogContent>
          {!showEditWarning ? (
            <>
              <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                Warning: This change will affect all items that use this modifier.
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Are you sure you want to proceed with editing this modifier?
              </Typography>
            </>
          ) : (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                value={editFormData.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                error={!!editNameError}
                helperText={editNameError || ''}
              />
              <TextField
                margin="dense"
                label="Price"
                type="number"
                fullWidth
                value={editFormData.price / 100 || ''}
                onChange={(e) => handleEditFormChange('price', e.target.value)}
                inputProps={{
                  min: 0,
                  step: 0.01,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{getCurrencySymbol(currency, locale)}</InputAdornment>,
                }}
              />
              <TextField
                margin="dense"
                label="Tax"
                type="number"
                fullWidth
                value={editFormData.tax / 10 || ''}
                onChange={(e) => handleEditFormChange('tax', e.target.value)}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.1,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">%</InputAdornment>,
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="primary">
            Cancel
          </Button>
          {!showEditWarning ? (
            <Button onClick={handleProceedWithEdit} color="primary" variant="contained">
              Proceed
            </Button>
          ) : (
            <Button onClick={handleConfirmEdit} color="primary" variant="contained">
              Save Changes
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withStyles(styles)(ModifiersTransferList);
